import { Injectable } from '@angular/core';
import { webDtos } from '@vectronic/openApi-admin';
import { SchedulePreview, ScheduleTimpespanPreview, utcMoment } from '@vectronic/util';
import moment, { Moment } from 'moment';
import { ScheduleTypeConfig } from './abstract-schedule.service';

@Injectable({ providedIn: 'root' })
export class ScheduleCalendarCalculationService {
  getRange(date: Moment, of: 'month' | 'day') {
    return { start: utcMoment(date).startOf(of), end: utcMoment(date).endOf(of) };
  }

  numberOfSchedulesInMonth(rule: webDtos.ScheduleRule, date: Moment, config: ScheduleTypeConfig) {
    const { start, end } = this.getRange(date, 'month');
    return this.numberOfSchedulesInRange(rule, start, end, config);
  }

  schedulesInDay(
    rule: webDtos.ScheduleRule,
    date: Moment,
    index: number,
    config: ScheduleTypeConfig,
    fixPerTransmission?: number,
  ) {
    const { start, end } = this.getRange(date, 'day');
    return this.schedulesInRange(rule, start, end, index, config, fixPerTransmission);
  }

  timespanInDay(rule: webDtos.ScheduleRule, date: Moment, config: ScheduleTypeConfig) {
    const { start, end } = this.getRange(date, 'day');
    return this.timespanInRange(rule, start, end, config);
  }

  minToMs(mins: number) {
    return mins * 60 * 1000;
  }

  msToMin(ms: number) {
    return ms / (60 * 1000);
  }

  positive(n: number) {
    return Math.max(n, 0);
  }

  getMs(
    rule: webDtos.ScheduleRule,
    startRange: Moment,
    endRange: Moment,
    config: ScheduleTypeConfig,
  ) {
    if (!rule?.periodLength) return;
    const { startTime, endTime, sequences, periodLength } = rule;
    for (const { fixRate, duration } of sequences) {
      if (((config.hasFixRate || config.hasCommunicationInterval) && !fixRate) || !duration) return;
    }
    const startMs = utcMoment(startTime).startOf('day').valueOf();
    const endMs = utcMoment(endTime).endOf('day').valueOf();
    const startRangeMs = startRange.valueOf();
    const endRangeMs = endRange.valueOf();
    if (startRangeMs > endMs || endRangeMs < startMs) return;
    const intervalMs = this.minToMs(periodLength);
    const startInterval = this.positive(Math.floor((startRangeMs - startMs) / intervalMs));
    const endInterval = Math.min(
      Math.ceil((endRangeMs - startMs) / intervalMs),
      Math.ceil((endMs - startMs) / intervalMs),
    );
    return { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval };
  }

  getIntervalMs(
    { fixRate, offset, duration }: webDtos.ScheduleSequence,
    config: ScheduleTypeConfig,
  ) {
    let _fixRate = 60;
    if (config.hasFixRate || config.hasCommunicationInterval) _fixRate = fixRate / 60;
    const offsetMs = this.minToMs(offset);
    const durationMs = this.minToMs(duration);
    return { fixRate: _fixRate, offsetMs, durationMs };
  }

  timespanInRange(
    rule: webDtos.ScheduleRule,
    startRange: Moment,
    endRange: Moment,
    config: ScheduleTypeConfig,
  ) {
    const schedulesTimespan: ScheduleTimpespanPreview[] = [];
    const ms = this.getMs(rule, startRange, endRange, config);
    if (!ms) return schedulesTimespan;
    const { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval } = ms;
    const rangeDurationMs = endRangeMs - startRangeMs;
    // start one interval earlier so sequences overlapping into the next period are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const startIntervalMillis = startMs + i * intervalMs;
      this.collectSequenceTimespans(
        rule,
        config,
        startIntervalMillis,
        startRangeMs,
        endRangeMs,
        rangeDurationMs,
        schedulesTimespan,
      );
    }
    return schedulesTimespan;
  }

  private collectSequenceTimespans(
    rule: webDtos.ScheduleRule,
    config: ScheduleTypeConfig,
    startIntervalMillis: number,
    startRangeMs: number,
    endRangeMs: number,
    rangeDurationMs: number,
    schedulesTimespan: ScheduleTimpespanPreview[],
  ) {
    for (const sequence of rule.sequences) {
      this.collectTimespansForSequence(
        rule,
        sequence,
        config,
        startIntervalMillis,
        startRangeMs,
        endRangeMs,
        rangeDurationMs,
        schedulesTimespan,
      );
    }
  }

  private collectTimespansForSequence(
    rule: webDtos.ScheduleRule,
    sequence: webDtos.ScheduleSequence,
    config: ScheduleTypeConfig,
    startIntervalMillis: number,
    startRangeMs: number,
    endRangeMs: number,
    rangeDurationMs: number,
    schedulesTimespan: ScheduleTimpespanPreview[],
  ) {
    const { offsetMs, durationMs } = this.getIntervalMs(sequence, config);
    const periodMs = this.minToMs(rule.periodLength);
    let scheduleMs = startIntervalMillis;
    let inSequenceCount = 0;
    while (scheduleMs < endRangeMs) {
      const newStartRangeMs = startRangeMs + inSequenceCount * periodMs;
      if (newStartRangeMs >= endRangeMs) break;
      const offScheduleMs = scheduleMs + offsetMs;
      const newEndRangeMs = offScheduleMs + durationMs;
      const offset = inSequenceCount * rule.periodLength;
      const length = this.msToMin(rangeDurationMs);
      const isNewRangeInOffset = newStartRangeMs >= scheduleMs && newStartRangeMs < offScheduleMs;
      const isNewRangeAboveOffset =
        newStartRangeMs >= offScheduleMs && newStartRangeMs < newEndRangeMs;
      if (isNewRangeInOffset) {
        this.pushInOffsetTimespan(
          schedulesTimespan,
          offset,
          length,
          offScheduleMs,
          newStartRangeMs,
          newEndRangeMs,
          rangeDurationMs,
        );
      }
      if (isNewRangeAboveOffset) {
        this.pushAboveOffsetTimespan(
          schedulesTimespan,
          offset,
          length,
          newStartRangeMs,
          newEndRangeMs,
          rangeDurationMs,
        );
      }
      inSequenceCount++;
      scheduleMs += this.minToMs(rule.periodLength);
    }
  }

  private pushInOffsetTimespan(
    schedulesTimespan: ScheduleTimpespanPreview[],
    offset: number,
    length: number,
    offScheduleMs: number,
    newStartRangeMs: number,
    newEndRangeMs: number,
    rangeDurationMs: number,
  ) {
    const newOffsetMs = this.positive(offScheduleMs - newStartRangeMs);
    const newDurationMs = newEndRangeMs - newStartRangeMs - newOffsetMs;
    if (newOffsetMs < rangeDurationMs) {
      const duration = this.msToMin(
        this.positive(Math.min(newDurationMs, rangeDurationMs - newOffsetMs)),
      );
      offset += this.msToMin(newOffsetMs);
      schedulesTimespan.push({ offset, length, duration });
    }
  }

  private pushAboveOffsetTimespan(
    schedulesTimespan: ScheduleTimpespanPreview[],
    offset: number,
    length: number,
    newStartRangeMs: number,
    newEndRangeMs: number,
    rangeDurationMs: number,
  ) {
    const newDurationMs = newEndRangeMs - newStartRangeMs;
    const duration = this.msToMin(this.positive(Math.min(newDurationMs, rangeDurationMs)));
    schedulesTimespan.push({ offset, length, duration });
  }

  isSkipped(skipCount: number, inSequenceCount: number) {
    return skipCount && inSequenceCount % (skipCount + 1) !== 0;
  }

  isTransmitted(inSequenceCount: number, skipPosition = 0, fixPerTransmission = 1) {
    if (fixPerTransmission === 0) return false;
    const cycleLength = (skipPosition || 0) + 1;
    const cycleIndex = Math.floor(inSequenceCount / cycleLength);
    return (
      (cycleIndex + 1) % (fixPerTransmission || 1) === 0 && inSequenceCount % cycleLength === 0
    );
  }

  schedulesInRange(
    rule: webDtos.ScheduleRule,
    startRange: Moment,
    endRange: Moment,
    index: number,
    config: ScheduleTypeConfig,
    fixPerTransmission?: number,
  ) {
    const schedules: SchedulePreview[] = [];
    const ms = this.getMs(rule, startRange, endRange, config);
    if (!ms) return schedules;
    const { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval } = ms;
    // start one interval earlier so sequences overlapping into the next period are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const startIntervalMs = startMs + i * intervalMs;
      for (const sequence of rule.sequences) {
        const { fixRate, offsetMs, durationMs } = this.getIntervalMs(sequence, config);
        const endIntervalMs = startIntervalMs + durationMs;
        let scheduleMs = startIntervalMs + offsetMs;
        let inSequenceCount = 0;
        while (scheduleMs < endIntervalMs + offsetMs) {
          if (scheduleMs >= startRangeMs && scheduleMs < endRangeMs) {
            // The skip count is calculated per sequence and does not remember the previous period,
            // so every period starts again with a kept (green) fix
            const isSkipped = this.isSkipped(rule.skipCount, inSequenceCount);
            const isTransmitted = this.isTransmitted(
              inSequenceCount,
              rule.skipCount,
              fixPerTransmission,
            );
            schedules.push({ millis: scheduleMs, isSkipped, isTransmitted });
          }
          inSequenceCount++;
          scheduleMs += this.minToMs(fixRate);
        }
      }
    }
    return schedules;
  }

  numberOfSchedulesInRange(
    rule: webDtos.ScheduleRule,
    startRange: Moment,
    endRange: Moment,
    config: ScheduleTypeConfig,
  ) {
    let schedulesCount = 0;
    const ms = this.getMs(rule, startRange, endRange, config);
    if (!ms) return schedulesCount;
    const { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval } = ms;
    // start one interval earlier so sequences overlapping into the next period are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const startIntervalMs = startMs + i * intervalMs;
      for (const sequence of rule.sequences) {
        const { fixRate, offsetMs, durationMs } = this.getIntervalMs(sequence, config);
        const endIntervalMs = startIntervalMs + durationMs;
        let scheduleMs = startIntervalMs + offsetMs;
        while (scheduleMs < endIntervalMs + offsetMs) {
          if (scheduleMs >= startRangeMs && scheduleMs < endRangeMs) schedulesCount++;
          scheduleMs += this.minToMs(fixRate);
        }
      }
    }
    return schedulesCount;
  }

  isEndDateAfterStartDate(startDate: string, endDate: string) {
    return utcMoment(endDate).isSameOrAfter(utcMoment(startDate));
  }

  hasRuleFromNowToThreeYears(rules: webDtos.ScheduleRule[]) {
    // Calculate the end date as three years from now
    const endDate = moment().add(3, 'years');

    // Create an array to represent each day from now to three years later
    const allDays = [];
    const currentDate = moment();

    while (currentDate.isBefore(endDate, 'day') || currentDate.isSame(endDate, 'day')) {
      allDays.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }

    // Check if all days are covered by the given time ranges
    for (const range of rules) {
      const startTime = moment(range.startTime);
      const endTime = moment(range.endTime);

      // Check each day in the range
      for (
        let current = startTime;
        current.isBefore(endTime, 'day') || current.isSame(endTime, 'day');
        current.add(1, 'day')
      ) {
        const currentDate = current.format('YYYY-MM-DD');
        const index = allDays.indexOf(currentDate);

        // If the current day is found, remove it from the list
        if (index !== -1) {
          allDays.splice(index, 1);
        }
      }
    }
    // If all days are covered, the length of all Days should be 0
    return allDays.length === 0;
  }
}
